/*
 * "Hide when scrolling down, return when scrolling up" for the SEP2026 header.
 *
 * Only runs when the theme setting Header (SEP2026) → Sticky header is set to
 * that mode, which puts `aw-sticky-header--scroll-up` on the body. The other
 * two modes need no script: `none` leaves the header in flow, and `always` is
 * one CSS rule.
 *
 * The state machine is the same shape as Dawn's <sticky-header>
 * (sections/header.liquid:367-412), which this cannot reuse — that element is
 * defined inside header.liquid's inline script, and the SEP2026 templates render
 * their own header group instead.
 *
 *   above the header's own bottom edge   -> released, in flow
 *   below it, travelling down            -> pinned and translated away
 *   below it, travelling up              -> pinned and revealed, animated
 *
 * This script publishes nothing. --aw-pinned-header-height is CSS-only and is
 * deliberately NOT set in this mode (aw-header-sep2026.css) — the header here
 * is a transient overlay, so the jeans page's pinned photograph clears only its
 * own 32px and lets the header pass over it. Tracking the header's position
 * from here would move that photograph by 108px on every change of scroll
 * direction; publishing a constant 108px held it where the header used to be
 * and made it wait there. Neither is wanted.
 */
(function () {
  'use strict';

  var SCROLL_UP_CLASS = 'aw-sticky-header--scroll-up';

  function initStickyHeader() {
    if (!document.body.classList.contains(SCROLL_UP_CLASS)) return;

    var header = document.querySelector('.aw-head');
    if (!header) return;

    /*
     * `position: fixed` takes the header out of flow, so the content below it
     * would jump up by its height at the moment it pins. The shim occupies that
     * space instead. Inserted here rather than in the section's markup because
     * it exists only for this mode.
     */
    var shim = document.createElement('div');
    shim.className = 'aw-head__shim';
    shim.setAttribute('aria-hidden', 'true');
    header.parentNode.insertBefore(shim, header.nextSibling);

    var pinned = false;
    var hidden = false;
    var lastY = window.pageYOffset;
    var headerTop = 0;
    var headerBottom = 0;

    /*
     * Two bounds, not one, and they do different jobs — see onScroll. Measured
     * from the released position, so they are the header's offsets in the
     * document rather than its position on screen; reading them while pinned
     * would give 0 and the header would never release.
     */
    function measure() {
      if (pinned) return;
      headerTop = header.offsetTop;
      headerBottom = headerTop + header.offsetHeight;
      shim.style.height = header.offsetHeight + 'px';
    }

    function pin() {
      if (pinned) return;
      pinned = true;
      header.classList.add('is-pinned');
      shim.classList.add('is-active');
    }

    function release() {
      if (!pinned) return;
      pinned = false;
      hidden = false;
      header.classList.remove('is-pinned', 'is-hidden', 'is-animated');
      shim.classList.remove('is-active');
    }

    function hide() {
      if (hidden) return;
      hidden = true;
      // No transition on the way out: it would animate from the resting
      // position the reader has already scrolled past.
      header.classList.remove('is-animated');
      header.classList.add('is-hidden');
    }

    function reveal() {
      if (!hidden) return;
      hidden = false;
      header.classList.add('is-animated');
      header.classList.remove('is-hidden');
    }

    /*
     * The two bounds are not interchangeable, and using the bottom for both is
     * what made the header repaint on the way back to the top: between the two
     * it would go back into flow while the page was still scrolled past it, so
     * it vanished and slid in again as the reader reached the top.
     *
     *   at or above headerTop     -> released, in flow. Static and fixed
     *                                coincide here, so nothing moves.
     *   below headerBottom        -> pinned, hidden or revealed by direction
     *   between the two           -> left exactly as it was
     *
     * Same split as Dawn's <sticky-header> (sections/header.liquid:372-389),
     * where no branch covers the gap either.
     */
    function onScroll() {
      var y = window.pageYOffset;

      if (y <= headerTop) {
        release();
      } else if (y > headerBottom) {
        pin();
        if (y > lastY) hide();
        else if (y < lastY) reveal();
      }

      lastY = y;
    }

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Width changes alter the header's height at the 800px and 700px steps.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(measure, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyHeader);
  } else {
    initStickyHeader();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('.aw-head')) initStickyHeader();
  });
})();
